import {
  Box,
  ClickAwayListener,
  FormControl,
  InputLabel,
  OutlinedInput,
  Paper,
  Popper,
  Stack,
  useTheme,
} from '@mui/material'
import { RichTreeView } from '@mui/x-tree-view'
import { useTreeViewApiRef } from '@mui/x-tree-view/hooks'
import type { TreeViewBaseItem } from '@mui/x-tree-view/models'
import type { FC } from 'react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Organization } from 'types/organization'
import type { UserProfile } from 'types/user-profile'
import { getUniqueOrganizationRoles } from 'types/user-profile'
import { TextField } from '../@extended/TextField'
import './SingleOrganizationSelector.css'

interface SingleOrganizationSelectorProps {
  userProfile: UserProfile
  selectedOrganizationId: string
  handleChange: (selected: string) => void
  className?: string
  style?: React.CSSProperties
  width?: string | number
  label?: string
  placeholder?: string
  disabled?: boolean
  memberRolesOnly?: boolean
  space?: number
  variant?: 'standard' | 'outlined'
  labelColor?: string
  labelBackgroundColor?: string
  disableAutoSelection?: boolean
}

const SingleOrganizationSelector: FC<SingleOrganizationSelectorProps> = ({
  userProfile,
  selectedOrganizationId,
  handleChange,
  className,
  style,
  width = '100%',
  label = 'Select Team',
  placeholder = 'Select Team',
  disabled = false,
  memberRolesOnly = false,
  space = 0,
  variant = 'outlined',
  labelColor = undefined,
  labelBackgroundColor = undefined,
  disableAutoSelection = false,
}) => {
  const theme = useTheme()
  const apiRef = useTreeViewApiRef()
  const anchorRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  const uniqueUserRoles = useMemo(
    () => getUniqueOrganizationRoles(userProfile, memberRolesOnly),
    [userProfile, memberRolesOnly]
  )

  // Organization map for efficient lookups
  const organizationMap = useMemo(() => {
    const map = new Map<string, Organization>()
    uniqueUserRoles.forEach((role) => {
      map.set(role.organization.id, role.organization)
    })
    return map
  }, [uniqueUserRoles])

  // Selected organization name for display
  const selectedOrganizationName = useMemo(() => {
    return organizationMap.get(selectedOrganizationId)?.name || ''
  }, [selectedOrganizationId, organizationMap])

  const handleItemSelectionToggle = useCallback(
    (event: React.SyntheticEvent, itemId: string, isSelected: boolean) => {
      if (isSelected) {
        handleChange(itemId)
        setOpen(false)
      }
    },
    [handleChange]
  )

  const treeData = useMemo((): TreeViewBaseItem[] => {
    const isTopLevelOrg = (org: Organization) => !org.parentId || !organizationMap.has(org.parentId)

    const buildTree = (org: Organization): TreeViewBaseItem => ({
      id: org.id,
      label: org.name,
      children: uniqueUserRoles
        .filter((role) => role.organization.parentId === org.id)
        .map((role) => buildTree(role.organization))
        .sort((a, b) => a.label.localeCompare(b.label)),
    })

    const rootOrganizations = uniqueUserRoles.map((role) => role.organization).filter(isTopLevelOrg)

    return rootOrganizations.map(buildTree).sort((a, b) => a.label.localeCompare(b.label))
  }, [uniqueUserRoles, organizationMap])

  const firstRootItemId = useMemo(() => {
    return treeData[0]?.id
  }, [treeData])

  const handleInputClick = useCallback(() => {
    if (!disabled) {
      setOpen((prev) => !prev)
    }
  }, [disabled])

  const handleClickAway = useCallback(() => {
    setOpen(false)
  }, [])

  const renderValue = () => {
    if (!selectedOrganizationName) {
      return (
        <Box
          sx={{
            color: theme.palette.text.secondary,
            width: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {placeholder}
        </Box>
      )
    }

    return (
      <Box
        sx={{
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: theme.palette.text.primary,
        }}
        title={selectedOrganizationName}
      >
        {selectedOrganizationName}
      </Box>
    )
  }

  useEffect(() => {
    if (!disableAutoSelection && selectedOrganizationId === '' && firstRootItemId) {
      handleChange(firstRootItemId)
    }
  }, [selectedOrganizationId, firstRootItemId, handleChange, disableAutoSelection])

  if (disabled) {
    return (
      <Stack spacing={space} sx={{ flexGrow: 1 }}>
        <TextField
          key="employee-organization-select"
          fullWidth
          variant="outlined"
          label={label}
          pill
          shrink
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
          value={selectedOrganizationName}
          sx={{
            '& .MuiInputBase-input': {
              color: 'text.secondary',
            },
          }}
        />
      </Stack>
    )
  }

  return (
    <div className={className} style={{ ...style, width }}>
      <FormControl variant={variant} sx={{ width }}>
        <InputLabel
          shrink
          variant={variant}
          sx={{
            '&.MuiInputLabel-shrink': {
              ...(labelBackgroundColor && { background: labelBackgroundColor }),
              color: labelColor || undefined,
              paddingLeft: '12px',
              paddingRight: '12px',
            },
          }}
        >
          {label}
        </InputLabel>

        <ClickAwayListener onClickAway={handleClickAway}>
          <div>
            <OutlinedInput
              ref={anchorRef}
              readOnly
              value=""
              onClick={handleInputClick}
              disabled={disabled}
              label={label}
              sx={{
                cursor: disabled ? 'default' : 'pointer',
                borderRadius: '21px',
                width,
                '& .MuiOutlinedInput-input': {
                  padding: '10.5px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
                '& .MuiOutlinedInput-root': {
                  width,
                },
              }}
              inputProps={{
                'aria-expanded': open,
                'aria-haspopup': 'listbox',
                'aria-label': label,
              }}
              startAdornment={renderValue()}
            />

            <Popper
              open={open}
              anchorEl={anchorRef.current}
              placement="bottom-start"
              sx={{ zIndex: theme.zIndex.modal }}
              modifiers={[
                {
                  name: 'flip',
                  enabled: true,
                },
                {
                  name: 'preventOverflow',
                  enabled: true,
                },
              ]}
            >
              <Paper
                sx={{
                  width: anchorRef.current?.offsetWidth || 'auto',
                  maxHeight: 300,
                  overflow: 'auto',
                  mt: 1,
                  boxShadow: theme.shadows[8],
                }}
              >
                <RichTreeView
                  multiSelect={false}
                  checkboxSelection
                  apiRef={apiRef}
                  items={treeData}
                  selectedItems={selectedOrganizationId}
                  onItemSelectionToggle={handleItemSelectionToggle}
                  defaultExpandedItems={firstRootItemId ? [firstRootItemId] : []}
                  sx={{
                    '& .MuiTreeItem-content:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                    '& .MuiTreeItem-content.Mui-selected:hover': {
                      backgroundColor: theme.palette.action.selected,
                    },
                  }}
                />
              </Paper>
            </Popper>
          </div>
        </ClickAwayListener>
      </FormControl>
    </div>
  )
}

export default SingleOrganizationSelector
